// The matter's file cabinet — Clio's Documents tab, held here instead.
//
// The rest of the Documents tab is about MAKING documents. This is
// about keeping them: the folder tree the firm already uses in Clio,
// with the actual files in it.
//
// Three things this screen has to get right, all of them about not
// lying:
//
//   If storage is not configured, say so BEFORE somebody picks a file,
//   and do not show an upload control that is going to fail. A refused
//   upload is fine; a refused upload discovered after choosing a 40MB
//   record is not.
//
//   An upload that fails says what happened and the file does not
//   appear in the tree. There is no optimistic row here on purpose —
//   showing a file that is not stored is the failure this whole
//   feature is built to avoid.
//
//   Files nobody filed are shown, not hidden. A document in no folder
//   is precisely the one that gets lost.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { C } from "../constants.js";
import { API, apiFetch } from "../api.js";

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

// One file row: name, the facts about it, and the three things you can
// do to it.
function FileRow({ file, onDownload, onRename, onDelete, busy }) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(file.name);

  if (renaming) {
    return React.createElement("div", {
      style: { display: "flex", gap: "6px", padding: "6px 0 6px 26px" },
    },
      React.createElement("input", {
        value: draft, autoFocus: true,
        onChange: (e) => setDraft(e.target.value),
        onKeyDown: (e) => {
          if (e.key === "Enter") { onRename(file, draft); setRenaming(false); }
          if (e.key === "Escape") { setDraft(file.name); setRenaming(false); }
        },
        style: {
          flex: 1, padding: "5px 8px", fontSize: "13px",
          border: `1px solid ${C.warmGray}`, borderRadius: "4px",
        },
      }),
      React.createElement("button", {
        onClick: () => { onRename(file, draft); setRenaming(false); },
        style: { fontSize: "12px", cursor: "pointer" },
      }, "Save"),
      React.createElement("button", {
        onClick: () => { setDraft(file.name); setRenaming(false); },
        style: { fontSize: "12px", cursor: "pointer" },
      }, "Cancel"),
    );
  }

  return React.createElement("div", {
    style: {
      display: "flex", alignItems: "baseline", gap: "10px",
      padding: "5px 0 5px 26px", fontSize: "13px",
      opacity: busy ? 0.5 : 1,
    },
  },
    React.createElement("button", {
      onClick: () => onDownload(file),
      title: "Download",
      style: {
        border: "none", background: "none", padding: 0, cursor: "pointer",
        color: C.navy, fontSize: "13px", textAlign: "left",
        textDecoration: "underline", textDecorationColor: C.warmGray,
      },
    }, file.name),

    // PHI is labelled, not colour-coded. The label is the whole point:
    // it says this read will be recorded.
    file.is_phi && React.createElement("span", {
      style: {
        fontSize: "10px", letterSpacing: "0.06em", color: C.textMuted,
        border: `1px solid ${C.warmGray}`, borderRadius: "3px",
        padding: "1px 5px", whiteSpace: "nowrap",
      },
      title: "Opening this file is recorded in the audit log.",
    }, "PHI"),

    React.createElement("span", {
      style: { color: C.textMuted, fontSize: "12px", whiteSpace: "nowrap" },
    }, [fmtSize(file.size_bytes), fmtDate(file.created_at), file.uploaded_by]
      .filter(Boolean).join(" · ")),

    React.createElement("span", { style: { flex: 1 } }),
    React.createElement("button", {
      onClick: () => setRenaming(true),
      style: {
        border: "none", background: "none", cursor: "pointer",
        color: C.textMuted, fontSize: "12px",
      },
    }, "Rename"),
    React.createElement("button", {
      onClick: () => onDelete(file),
      style: {
        border: "none", background: "none", cursor: "pointer",
        color: C.textMuted, fontSize: "12px",
      },
    }, "Remove"),
  );
}

function Folder({ node, depth, open, toggle, onUpload, canUpload, ...rest }) {
  const isOpen = open.has(node.id);
  return React.createElement(React.Fragment, { key: node.id },
    React.createElement("div", {
      style: {
        display: "flex", alignItems: "center", gap: "8px",
        padding: "6px 0", paddingLeft: `${depth * 18}px`,
        borderTop: depth === 0 ? `1px solid ${C.warmGray}` : "none",
      },
    },
      React.createElement("button", {
        onClick: () => toggle(node.id),
        style: {
          border: "none", background: "none", cursor: "pointer",
          padding: 0, width: "16px", color: C.textMuted, fontSize: "11px",
        },
      }, isOpen ? "▾" : "▸"),
      React.createElement("span", {
        onClick: () => toggle(node.id),
        style: {
          cursor: "pointer", fontSize: "13px",
          fontWeight: depth === 0 ? 600 : 500,
          color: node.file_count ? C.navy : C.textMuted,
        },
      }, node.name),
      // Count includes subfolders, so a collapsed folder says whether
      // opening it is worth the click.
      node.file_count > 0 && React.createElement("span", {
        style: { fontSize: "12px", color: C.textMuted },
      }, node.file_count),
      React.createElement("span", { style: { flex: 1 } }),
      canUpload && React.createElement("button", {
        onClick: () => onUpload(node.id),
        style: {
          border: "none", background: "none", cursor: "pointer",
          color: C.textMuted, fontSize: "12px",
        },
      }, "Add file"),
    ),
    isOpen && node.files.map(f =>
      React.createElement("div", { key: `f${f.id}`, style: { paddingLeft: `${depth * 18}px` } },
        React.createElement(FileRow, { file: f, ...rest }))),
    isOpen && node.children.map(c =>
      React.createElement(Folder, {
        key: c.id, node: c, depth: depth + 1, open, toggle,
        onUpload, canUpload, ...rest,
      })),
  );
}

export function CaseFileCard({ matterId, showNotification }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileInput = useRef(null);
  const targetFolder = useRef(null);
  const [survey, setSurvey] = useState(null);
  const [removed, setRemoved] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [importing, setImporting] = useState(null);
  const cancelImport = useRef(false);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/api/matters/${matterId}/case-files`));
    } catch (e) {
      setData({ error: e.message });
    }
    // Fetched alongside the tree so the count can be shown without a
    // click. Its own try: a removed-files failure must not blank the
    // tree, which is what somebody actually came here for.
    try {
      const r = await apiFetch(`/api/matters/${matterId}/case-files/deleted`);
      setRemoved(r.data || []);
    } catch {
      setRemoved([]);
    }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const notify = (msg, kind) =>
    showNotification && showNotification(msg, kind);

  const provision = async () => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/api/matters/${matterId}/case-files/provision`, { method: "POST" });
      notify(`${res.folders_created} folders created.`, "success");
      await load();
    } catch (e) { notify(e.message, "error"); }
    setBusy(false);
  };

  const pickFile = (folderId) => {
    targetFolder.current = folderId;
    fileInput.current && fileInput.current.click();
  };

  const doUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";              // so the same file can be re-picked
    if (!file) return;

    setUploading(file.name);
    try {
      const body = new FormData();
      body.append("file", file);
      if (targetFolder.current != null)
        body.append("folder_id", String(targetFolder.current));
      await apiFetch(`/api/matters/${matterId}/case-files/upload`,
                     { method: "POST", body });
      notify(`${file.name} filed.`, "success");
      // Reloaded rather than inserted optimistically: the tree must
      // only ever show files that are actually stored.
      await load();
    } catch (err) {
      notify(err.message, "error");
    }
    setUploading("");
  };

  const download = async (file) => {
    // A plain link would work, but the endpoint can answer 409 on a
    // checksum mismatch, and a browser handed a 409 body would save it
    // as the file. Fetching first means a corrupted object surfaces as
    // a message instead of a broken download.
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/case-files/${file.id}/download`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Could not open ${file.name}.`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { notify(e.message, "error"); }
    setBusy(false);
  };

  const rename = async (file, name) => {
    if (!name.trim() || name === file.name) return;
    try {
      await apiFetch(`/api/case-files/${file.id}`, {
        method: "PATCH", body: JSON.stringify({ name }),
      });
      await load();
    } catch (e) { notify(e.message, "error"); }
  };

  const remove = async (file) => {
    if (!window.confirm(
      `Remove ${file.name} from this matter?\n\n` +
      `The file itself is kept and can be restored — nothing here is ` +
      `permanently deleted.`)) return;
    try {
      await apiFetch(`/api/case-files/${file.id}`, { method: "DELETE" });
      notify(`${file.name} removed.`, "success");
      await load();
    } catch (e) { notify(e.message, "error"); }
  };

  // Bringing the matter across from Clio. Surveyed first, because the
  // firm should see what is about to happen — how many files, how much
  // data, and which ones cannot come — before anything moves.
  const runSurvey = async () => {
    setBusy(true);
    try {
      setSurvey(await apiFetch(`/api/matters/${matterId}/clio-import/survey`));
    } catch (e) { notify(e.message, "error"); }
    setBusy(false);
  };

  const runImport = async () => {
    cancelImport.current = false;
    let copied = 0, failed = 0, guard = 0;
    setImporting({ copied: 0, total: survey ? survey.to_import : 0 });
    try {
      // Batched by the server, so each request stays short. Loop until
      // nothing is left rather than trusting a single call to finish a
      // 272-document matter inside one request timeout.
      // The guard is a backstop: if a batch ever reported progress it
      // had not made, this would otherwise spin forever.
      while (guard++ < 400) {
        if (cancelImport.current) break;
        const r = await apiFetch(
          `/api/matters/${matterId}/clio-import`, { method: "POST" });
        copied += r.imported;
        failed += (r.failed || []).length;
        setImporting({ copied, total: copied + r.remaining });
        if (!r.remaining) break;
        if (!r.imported && !(r.failed || []).length) break;  // no progress
      }
      await load();
      await runSurvey();
      notify(
        failed
          ? `Copied ${copied} file(s); ${failed} could not be copied.`
          : `Copied ${copied} file(s) from Clio.`,
        failed ? "error" : "success");
    } catch (e) {
      notify(`Import stopped: ${e.message}`, "error");
      await load();
    }
    setImporting(null);
  };

  const restore = async (file) => {
    try {
      await apiFetch(`/api/case-files/${file.id}/restore`, { method: "POST" });
      notify(`${file.name} restored.`, "success");
      await load();
    } catch (e) { notify(e.message, "error"); }
  };

  const toggle = (id) => setOpen(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  if (!data) return null;

  const storageReady = data.storage && data.storage.configured;
  const rowProps = {
    onDownload: download, onRename: rename, onDelete: remove, busy,
  };

  return React.createElement("div", {
    style: {
      background: "#fff", border: `1px solid ${C.warmGray}`,
      borderRadius: "8px", padding: "20px 24px", marginBottom: "20px",
    },
  },
    React.createElement("div", {
      style: {
        display: "flex", alignItems: "baseline", gap: "12px",
        marginBottom: "4px",
      },
    },
      React.createElement("h3", {
        style: { margin: 0, fontSize: "15px", color: C.navy },
      }, "Case File"),
      React.createElement("span", {
        style: { flex: 1 },
      }),
      data.total_files > 0 && React.createElement("span", {
        style: { fontSize: "12px", color: C.textMuted },
      }, `${data.total_files} file${data.total_files === 1 ? "" : "s"}`),
    ),
    React.createElement("p", {
      style: { margin: "0 0 14px", fontSize: "12px", color: C.textMuted },
    }, "The matter's folders and documents, held here rather than in Clio."),

    data.error && React.createElement("p", {
      style: { fontSize: "13px", color: C.navy },
    }, data.error),

    // Said before anybody picks a file, not after.
    !storageReady && React.createElement("div", {
      style: {
        border: `1px solid ${C.warmGray}`, borderRadius: "6px",
        padding: "12px 14px", marginBottom: "14px",
        fontSize: "13px", color: C.navy, background: C.cream,
      },
    }, data.storage ? data.storage.detail : "Storage is not configured."),

    React.createElement("input", {
      ref: fileInput, type: "file", onChange: doUpload,
      style: { display: "none" },
    }),

    uploading && React.createElement("p", {
      style: { fontSize: "13px", color: C.textMuted, margin: "0 0 10px" },
    }, `Uploading ${uploading}…`),

    !data.provisioned && React.createElement("div", null,
      React.createElement("p", {
        style: { fontSize: "13px", color: C.textMuted },
      }, "No folders yet on this matter."),
      React.createElement("button", {
        onClick: provision, disabled: busy,
        style: {
          padding: "8px 16px", fontSize: "13px", cursor: "pointer",
          background: C.navy, color: "#fff", border: "none",
          borderRadius: "5px",
        },
      }, busy ? "Creating…" : "Create the standard folders"),
    ),

    data.provisioned && React.createElement("div", null,
      (data.tree || []).map(node =>
        React.createElement(Folder, {
          key: node.id, node, depth: 0, open, toggle,
          onUpload: pickFile, canUpload: storageReady, ...rowProps,
        })),
    ),

    // Never collapsed, never hidden. A file in no folder is the one
    // that gets lost, so it sits at the bottom asking to be filed.
    (data.unfiled || []).length > 0 && React.createElement("div", {
      style: { marginTop: "16px", borderTop: `1px solid ${C.warmGray}`, paddingTop: "10px" },
    },
      React.createElement("div", {
        style: { fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "4px" },
      }, `Not filed (${data.unfiled.length})`),
      data.unfiled.map(f =>
        React.createElement(FileRow, { key: f.id, file: f, ...rowProps })),
    ),

    // Removed files. Collapsed by default because this is history
    // rather than work, but the COUNT is always visible — a recycle
    // bin nobody can see is just a slower delete, which is what this
    // was until the restore existed.
    (removed || []).length > 0 && React.createElement("div", {
      style: { marginTop: "16px", borderTop: `1px solid ${C.warmGray}`, paddingTop: "10px" },
    },
      React.createElement("button", {
        onClick: () => setShowRemoved(v => !v),
        style: {
          border: "none", background: "none", padding: 0, cursor: "pointer",
          fontSize: "13px", fontWeight: 600, color: C.navy,
        },
      }, `${showRemoved ? "▾" : "▸"} Removed (${removed.length})`),

      showRemoved && React.createElement("div", { style: { marginTop: "6px" } },
        React.createElement("p", {
          style: { fontSize: "12px", color: C.textMuted, margin: "0 0 8px" },
        }, "Nothing here was deleted. The file is kept and can be put back."),
        removed.map(f => React.createElement("div", {
          key: f.id,
          style: {
            display: "flex", alignItems: "baseline", gap: "10px",
            padding: "5px 0 5px 26px", fontSize: "13px",
          },
        },
          React.createElement("span", { style: { color: C.textMuted } }, f.name),
          f.is_phi && React.createElement("span", {
            style: {
              fontSize: "10px", letterSpacing: "0.06em", color: C.textMuted,
              border: `1px solid ${C.warmGray}`, borderRadius: "3px",
              padding: "1px 5px",
            },
          }, "PHI"),
          React.createElement("span", {
            style: { color: C.textMuted, fontSize: "12px" },
          }, [f.deleted_by && `removed by ${f.deleted_by}`, fmtDate(f.deleted_at)]
            .filter(Boolean).join(" · ")),
          React.createElement("span", { style: { flex: 1 } }),
          React.createElement("button", {
            onClick: () => restore(f),
            style: {
              border: "none", background: "none", cursor: "pointer",
              color: C.navy, fontSize: "12px",
            },
          }, "Restore"),
        )),
      ),
    ),

    // ── Clio ──────────────────────────────────────────────────────
    // Below the tree, because the tree is what somebody came here for.
    storageReady && React.createElement("div", {
      style: {
        marginTop: "18px", paddingTop: "14px",
        borderTop: `1px solid ${C.warmGray}`,
      },
    },
      React.createElement("div", {
        style: { fontSize: "13px", fontWeight: 600, color: C.navy },
      }, "Bring this matter across from Clio"),
      React.createElement("p", {
        style: { margin: "3px 0 10px", fontSize: "12px", color: C.textMuted },
      }, "Copies the documents and keeps the folders as they are in Clio. "
       + "Nothing in Clio is changed, and running it again picks up "
       + "where it stopped."),

      importing && React.createElement("p", {
        style: { fontSize: "13px", color: C.navy, margin: "0 0 10px" },
      }, `Copying… ${importing.copied}`
       + (importing.total ? ` of ${importing.total}` : "")),

      survey && !importing && React.createElement("div", {
        style: { fontSize: "13px", color: C.navy, marginBottom: "10px" },
      },
        React.createElement("div", null,
          `${survey.clio_documents} document(s) in Clio · `
          + `${survey.already_here} already here · `
          + `${survey.to_import} to copy`
          + (survey.pending_bytes
              ? ` (${(survey.pending_bytes / (1024 * 1024)).toFixed(0)} MB)`
              : "")),
        // Named, not counted. "3 files are too large" is not something
        // anybody can act on.
        (survey.too_large || []).length > 0 && React.createElement("div", {
          style: { marginTop: "6px", fontSize: "12px", color: C.textMuted },
        },
          React.createElement("div", null,
            `${survey.too_large.length} file(s) are over the `
            + `${Math.round(survey.max_upload_bytes / (1024 * 1024))}MB limit `
            + `and will not be copied:`),
          survey.too_large.slice(0, 8).map((t, i) =>
            React.createElement("div", { key: i, style: { paddingLeft: "10px" } },
              `${t.name} — ${(t.size_bytes / (1024 * 1024)).toFixed(1)} MB`)),
          survey.too_large.length > 8 && React.createElement("div", {
            style: { paddingLeft: "10px" },
          }, `and ${survey.too_large.length - 8} more`),
        ),
      ),

      React.createElement("button", {
        onClick: runSurvey, disabled: busy || !!importing,
        style: {
          padding: "7px 14px", fontSize: "13px", cursor: "pointer",
          background: "transparent", color: C.navy,
          border: `1px solid ${C.warmGray}`, borderRadius: "5px",
        },
      }, busy ? "Checking…" : survey ? "Re-check Clio" : "Check what is in Clio"),

      survey && survey.to_import > 0 && React.createElement("button", {
        onClick: importing ? () => { cancelImport.current = true; } : runImport,
        disabled: busy,
        style: {
          marginLeft: "8px", padding: "7px 14px", fontSize: "13px",
          cursor: "pointer",
          background: importing ? "transparent" : C.navy,
          color: importing ? C.navy : "#fff",
          border: importing ? `1px solid ${C.warmGray}` : "none",
          borderRadius: "5px",
        },
      }, importing ? "Stop" : `Copy ${survey.to_import} file(s) across`),
    ),

    data.provisioned && storageReady && React.createElement("div", {
      style: { marginTop: "14px" },
    },
      React.createElement("button", {
        onClick: () => pickFile(null),
        style: {
          padding: "7px 14px", fontSize: "13px", cursor: "pointer",
          background: "transparent", color: C.navy,
          border: `1px solid ${C.warmGray}`, borderRadius: "5px",
        },
      }, "Upload without filing"),
      React.createElement("button", {
        onClick: provision, disabled: busy,
        style: {
          marginLeft: "8px", padding: "7px 14px", fontSize: "13px",
          cursor: "pointer", background: "transparent", color: C.textMuted,
          border: `1px solid ${C.warmGray}`, borderRadius: "5px",
        },
      }, "Add any missing folders"),
    ),
  );
}
